import {Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {FeatureTypeService} from '@catcore/services';
import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {FeaturePortConnectionService} from '@catmodules/models/feature-port/services/feature-port-connection.service';
import {FeaturePort, FeaturePortConnection} from '@catmodules/models/feature-port/types';
import {ActiveModal} from '@catmodules/widgets/slide-in-modal/utils/modal-ref';
import {StyleType} from '@catui/types';
import {DisplayType} from '@catuiform/types';
import {isNullOrUndefined} from 'util';
import {extractDataFromModel} from "@catcore/utils";
import {ProjectPortService} from "@catmodules/models/ports/services/project-port.service";
import {Subscription} from "rxjs/Subscription";

const componentBaseName = 'modal-create-port-connection';
const selectorName = 'cat-' + componentBaseName;
const defaultClassNames = [selectorName].join(' ');

/**
 * The component provides the functionality to display the detailed information for a FeaturePortConnection
 * and also the option to delete this model.
 */
@Component({
  templateUrl: './modify-port-connection.component.html',
  styleUrls: ['./modify-port-connection.component.scss'],
})
export class ModifyPortConnectionComponent implements OnInit, OnDestroy {
  @HostBinding('class') classNames = defaultClassNames;
  @Input() title: string;
  @Output() afterDelete = new EventEmitter();
  @Output() afterUpdate = new EventEmitter<FeaturePortConnection>();

  @Input() featureLevel: boolean = true;
  @Input() displayDelete: boolean = true;

  @Input()
  get model(): FeaturePortConnection {
    return this._model;
  }

  private optionsSubscription: Subscription;

  portOptions = [];

  private _port1;
  private _port2;

  set model(value: FeaturePortConnection) {
    this._model = value;
    if (!isNullOrUndefined(value)) {
      this.form.patchValue({
        speed: value.speed,
        timeout: value.timeout,
        port1: (this.featureLevel ? value._links.port_type1.href : value._links.port1.href),
        port2: (this.featureLevel ? value._links.port_type2.href : value._links.port2.href),
        no_advertise_speed: value.no_advertise_speed,
        to_be_removed: (this.featureLevel ? value.to_be_removed : false)
      });

      const port1Attr = (this.featureLevel ? 'port_type1' : 'port1');
      this._port1 = extractDataFromModel(this.portConnection, port1Attr, '_embedded');

      const port2Attr = (this.featureLevel ? 'port_type2' : 'port2');
      this._port2 = extractDataFromModel(this.portConnection, port2Attr, '_embedded');

      let fixedPort = null;
      let editablePort = null;
      if (value.port1_editable) {
        fixedPort = this._port2;
        editablePort = this._port1;
      }
      if (value.port2_editable) {
        fixedPort = this._port1;
        editablePort = this._port2;
      }
      if (fixedPort !== null && !this.featureLevel) {
        if (this.optionsSubscription) {
          this.optionsSubscription.unsubscribe();
        }
        this.optionsSubscription = this.projectPortService.getAllSubresources(fixedPort.id, 'matching-ports').subscribe((ports) => {
          this.portOptions = [];
          console.log('Found ports: ', ports);
          this.portOptions.push({
            label: editablePort.name,
            value: editablePort._links.self.href
          });
          ports.forEach((port) => {
            this.portOptions.push({
              label: port.name,
              value: port._links.self.href
            });
          });
          this.portOptions.sort((a, b) => (a.label < b.label ? (a.label === b.label ? 0 : -1) : 1));
        });
      }
    }
  }

  get dataIsReady() {
    const isReady = !isNullOrUndefined(this.model);
    return isReady;
  }

  get portConnection(): FeaturePortConnection {
    let portConnection = null;
    if (this.dataIsReady) {
      portConnection = this.model;
    }
    return portConnection;
  }

  get port1(): FeaturePort {
    return this._port1;
  }

  get port2(): FeaturePort {
    return this._port2;
  }

  form: FormGroup;

  labels = {
    name: 'Name',
    portType: 'Type',
    isInput: 'Is Input',
    isOutput: 'Is Output',
    isVirtual: 'Is Virtual',
    timeout: 'Timeout',
    speed: 'Speed',
    port1: 'Port 1',
    port2: 'Port 2',
    no_advertise_speed: 'No Advertise Speed',
  };

  controlRefs = {
    timeout: 'timeout',
    speed: 'speed',
    no_advertise_speed: 'no_advertise_speed',
  };

  styleType = StyleType;
  displayTypes = DisplayType;

  private _model: FeaturePortConnection;

  constructor(public el: ElementRef,
              private schemaService: EntitySchemaService,
              private formBuilder: FormBuilder,
              private featureService: FeatureTypeService,
              private featurePortConnectionService: FeaturePortConnectionService,
              private projectPortService: ProjectPortService,
              private genericResourceService: GenericResourceService,
              private activeModal: ActiveModal) {

    this.form = this.formBuilder.group({
      timeout: this.formBuilder.control('', []),
      speed: this.formBuilder.control('', []),
      no_advertise_speed: this.formBuilder.control('', []),
      port1: this.formBuilder.control('', []),
      port2: this.formBuilder.control('', []),
      to_be_removed: this.formBuilder.control('', [])
    });
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
  }

  update() {
    const service = this.genericResourceService.findServiceForResource(this.model._links.self.href);
    service
      .save(this.form.value)
      .subscribe(() => {
        this.afterUpdate.emit();
        this.activeModal.close(this.model);
      })
    ;
  }

  /**
   * Initialize a request to delete the current FeaturePortConnection
   */
  delete() {
    const service = this.genericResourceService.findServiceForResource(this.model._links.self.href);
    service
      .delete()
      .subscribe(() => {
        this.afterDelete.emit();
        this.activeModal.close();

      });

  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/feature-port/components/modify-port-connection/modify-port-connection.component.ts